import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import React from 'react';
import * as Yup from 'yup';
import {
  LegacyUser,
  OrganisationSummary,
  useCreateOrganisationMembership,
  useDeleteOrganisationMemberships,
  useListOrganisationMembers,
} from '../../../app/ApiGen';
import { Dialog } from '../../../components/Dialog';
import { errorSnackbar } from '../../../components/ErrorSnackbar';
import { SelectionMenu } from '../../../components/SelectionMenu';
import { Form, InputComponentProps, OnSubmit } from '../../../design-system';
import { createSchema } from '../../../utils/formUtils';
import { normalise, Normalised } from '../../../utils/request';

type UpdateMemberShipProps = {
  organisation: Normalised<OrganisationSummary>;
  user?: LegacyUser;
};

type UpdateMembershipFields = {
  roleAction: string;
};

const initialValue = {
  roleAction: '',
};

const validationSchema = createSchema<UpdateMembershipFields>({
  roleAction: Yup.string().required('Role action is required'),
});

export const UpdateMembership: React.FC<UpdateMemberShipProps> = ({ organisation, user }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isOpen, setOpened] = React.useState<boolean>(false);
  const updateMembership = useCreateOrganisationMembership({ organisationId: organisation.id });
  const currentOrganisationMembers = useListOrganisationMembers({ organisationId: organisation.id });
  const deleteMembership = useDeleteOrganisationMemberships({ organisationId: organisation.id });

  React.useEffect(() => {
    if (updateMembership.error) {
      errorSnackbar('Unable to add admin role', closeSnackbar, enqueueSnackbar, updateMembership.error);
    }

    if (currentOrganisationMembers.error) {
      errorSnackbar(
        'Unable to search organisation members to remove the role',
        closeSnackbar,
        enqueueSnackbar,
        currentOrganisationMembers.error,
      );
    }

    if (deleteMembership.error) {
      errorSnackbar('Unable to remove the admin role', closeSnackbar, enqueueSnackbar, updateMembership.error);
    }
  }, [closeSnackbar, enqueueSnackbar, updateMembership, deleteMembership, currentOrganisationMembers]);

  const onSubmit = React.useCallback<OnSubmit<UpdateMembershipFields>>(
    async ({ roleAction }, { setSubmitting }) => {
      try {
        if (roleAction === 'Add admin access') {
          await updateMembership.mutate({
            data: {
              relationships: {
                organisation: {
                  data: {
                    id: organisation.id,
                    type: 'organisations',
                  },
                },
                user: {
                  data: {
                    id: user?.id ?? '',
                    type: 'users',
                  },
                },
              },
              attributes: {
                isDefault: false,
                role: 0,
              },
              type: 'memberships',
            },
          });
          enqueueSnackbar(`Admin role added successfully`);
        } else {
          const memberships = currentOrganisationMembers.data ? normalise(currentOrganisationMembers.data) : [];
          const currentMembership = memberships.find((member) => user?.id === member.user?.id);

          if (currentMembership) {
            await deleteMembership.mutate({ data: [{ id: currentMembership.id, type: 'memberships' }] });
            enqueueSnackbar(`Admin role removed successfully`);
          }
        }
        setOpened(false);
      } finally {
        setSubmitting(false);
      }
    },
    [organisation, user, updateMembership, enqueueSnackbar, currentOrganisationMembers, deleteMembership],
  );

  return (
    <>
      <Button color="primary" onClick={() => setOpened(true)} variant="contained">
        Add/Remove admin
      </Button>
      <Dialog isOpen={isOpen} onClose={() => setOpened(false)} title="Role">
        <Form<UpdateMembershipFields>
          fields={[
            {
              id: 'roleAction',
              label: 'Role action',
              inputComponent: ({ disabled, error, name, onBlur, onChange }: InputComponentProps<string>) => (
                <SelectionMenu
                  disabled={disabled}
                  error={error}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  options={[{ value: 'Add admin access' }, { value: 'Remove admin access' }]}
                />
              ),
            },
          ]}
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          labels={{ save: 'Save' }}
          isEditing
        />
      </Dialog>
    </>
  );
};
